import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import { AlertCircle, Info, Server, Shield, Trash2, X } from 'lucide-react';
import sentinelNetwork from '@/lib/api/sentinelNetwork';
import ProjectMappingsEditor from './ProjectMappingsEditor';
import type { DataSourceItem } from './datasource-constants';

// ============================================================
// Edit Mappings Dialog
// ============================================================
export function EditMappingsDialog({
  open, platform, mappings, dataSources,
  onClose, onMappingsChange, onSave,
}: {
  open: boolean;
  platform: 'SonarQube' | 'BlackDuck' | 'Harbor' | null;
  mappings: Array<{ repoName: string; projectKey: string }>;
  dataSources: DataSourceItem[];
  onClose: () => void;
  onMappingsChange: (mappings: Array<{ repoName: string; projectKey: string }>) => void;
  onSave: () => void;
}) {
  const title = platform === 'SonarQube' ? 'SonarQube Project Mappings'
    : platform === 'BlackDuck' ? 'BlackDuck Project Mappings'
    : 'Trivy Image Mappings';
  const label = platform === 'SonarQube' ? 'Map Git repo names to SonarQube project keys'
    : platform === 'BlackDuck' ? 'Map Git repo names to BlackDuck project names'
    : 'Map Git repo names to Harbor image paths';
  const projectKeyLabel = platform === 'SonarQube' ? 'SonarQube project key (e.g., calea-frontend)'
    : platform === 'BlackDuck' ? 'BlackDuck project name'
    : 'Harbor image path (e.g., nerd/int/my-service)';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <Box sx={{ p: 3 }}>
        <Stack spacing={2}>
          <Typography variant="h6" fontWeight={600}>
            Edit {title}
          </Typography>
          <ProjectMappingsEditor
            mappings={mappings}
            onChange={onMappingsChange}
            label={label}
            projectKeyLabel={projectKeyLabel}
            gitRepoNames={dataSources.filter(ds => ds.platform === 'Git').map(ds => {
              const urlParts = ds.url.replace('.git', '').split('/');
              return urlParts[urlParts.length - 1];
            })}
          />
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button variant="outlined" startIcon={<X size={16} />} onClick={onClose}>Cancel</Button>
            <Button variant="contained" onClick={onSave}>Save Mappings</Button>
          </Stack>
        </Stack>
      </Box>
    </Dialog>
  );
}

// ============================================================
// SonarQube Report Dialog
// ============================================================
export function SonarReportDialog({
  open, loading, data, error, onClose,
}: {
  open: boolean;
  loading: boolean;
  data: any;
  error: string;
  onClose: () => void;
}) {
  const theme = useTheme();
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <Box sx={{ p: 3 }}>
        <Stack spacing={2}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Server size={24} color={theme.palette.primary.main} />
            <Typography variant="h6" fontWeight={600}>SonarQube Report</Typography>
          </Stack>
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}
          {error && <Alert severity="error">{error}</Alert>}
          {data && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Organization: <strong>{data.organization}</strong> | Fetched: {new Date(data.fetchedAt).toLocaleString()}
              </Typography>
              {data.projects?.length === 0 ? (
                <Alert severity="info">No projects found matching your Git repositories.</Alert>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Repository</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Branch</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="center">Bugs</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="center">Vulnerabilities</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="center">Code Smells</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="center">Hotspots</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.projects?.map((proj: any, idx: number) => (
                        <TableRow key={idx}>
                          <TableCell>
                            {proj.found ? (
                              <Typography variant="body2">{proj.repoName}</Typography>
                            ) : (
                              <Typography variant="body2" color="text.secondary">{proj.repoName} <Chip label="Not found" size="small" sx={{ ml: 1, fontSize: 10 }} /></Typography>
                            )}
                          </TableCell>
                          <TableCell>{proj.branch || '-'}</TableCell>
                          <TableCell align="center">{proj.found ? proj.metrics?.bugs ?? '-' : '-'}</TableCell>
                          <TableCell align="center">{proj.found ? proj.metrics?.vulnerabilities ?? '-' : '-'}</TableCell>
                          <TableCell align="center">{proj.found ? proj.metrics?.codeSmells ?? '-' : '-'}</TableCell>
                          <TableCell align="center">{proj.found ? proj.metrics?.securityHotspots ?? '-' : '-'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}
          <Stack direction="row" justifyContent="flex-end">
            <Button variant="outlined" onClick={onClose}>Close</Button>
          </Stack>
        </Stack>
      </Box>
    </Dialog>
  );
}

// ============================================================
// BlackDuck Report Dialog
// ============================================================
export function BlackDuckReportDialog({
  open, loading, data, error, onClose,
}: {
  open: boolean;
  loading: boolean;
  data: any;
  error: string;
  onClose: () => void;
}) {
  const theme = useTheme();
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <Box sx={{ p: 3 }}>
        <Stack spacing={2}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Shield size={24} color={theme.palette.warning.main} />
            <Typography variant="h6" fontWeight={600}>BlackDuck Report</Typography>
          </Stack>
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}
          {error && <Alert severity="error">{error}</Alert>}
          {data && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Fetched: {new Date(data.fetchedAt).toLocaleString()}
              </Typography>
              {data.projects?.length === 0 ? (
                <Alert severity="info">No projects found matching your Git repositories.</Alert>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Repository</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Version</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="center">Critical</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="center">High</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="center">Medium</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="center">Low</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.projects?.map((proj: any, idx: number) => (
                        <TableRow key={idx}>
                          <TableCell>
                            {/* Repo slug; tooltip reveals the mapped BD project key */}
                            <Tooltip
                              arrow
                              title={proj.bdKey ? (
                                <Typography variant="caption">BD Project: {proj.bdKey}</Typography>
                              ) : ''}
                            >
                              <Typography variant="body2" sx={{
                                cursor: proj.bdKey ? 'help' : 'default',
                                textDecoration: proj.bdKey ? 'underline' : 'none',
                                textDecorationStyle: 'dotted',
                                textDecorationColor: 'text.disabled',
                              }}>
                                {proj.repoName}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                          {proj.found ? (
                            <>
                              <TableCell>{proj.versionName || '-'}</TableCell>
                              <TableCell align="center" sx={{ color: proj.vulnerabilities?.critical > 0 ? 'error.main' : 'inherit', fontWeight: proj.vulnerabilities?.critical > 0 ? 600 : 400 }}>
                                {proj.vulnerabilities?.critical ?? '-'}
                              </TableCell>
                              <TableCell align="center" sx={{ color: proj.vulnerabilities?.high > 0 ? 'warning.main' : 'inherit', fontWeight: proj.vulnerabilities?.high > 0 ? 600 : 400 }}>
                                {proj.vulnerabilities?.high ?? '-'}
                              </TableCell>
                              <TableCell align="center">{proj.vulnerabilities?.medium ?? '-'}</TableCell>
                              <TableCell align="center">{proj.vulnerabilities?.low ?? '-'}</TableCell>
                            </>
                          ) : (
                            <TableCell colSpan={5} sx={{ color: 'text.disabled', fontStyle: 'italic', fontSize: 12 }}>
                              {proj.message || 'No report found for this repository'}
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
              {/* Info note for BD projects that had no matching git repo */}
              {data.unmapped?.length > 0 && (
                <Alert
                  severity="info"
                  icon={<Info size={16} />}
                  sx={{ mt: 1.5, py: 0.5, fontSize: 12 }}
                >
                  The following BlackDuck project{data.unmapped.length > 1 ? 's have' : ' has'} no
                  valid Git repo mapping and {data.unmapped.length > 1 ? 'were' : 'was'} excluded
                  from this report:{' '}
                  <strong>{data.unmapped.map((u: any) => u.projectKey).join(', ')}</strong>
                </Alert>
              )}
            </Box>
          )}
          <Stack direction="row" justifyContent="flex-end">
            <Button variant="outlined" onClick={onClose}>Close</Button>
          </Stack>
        </Stack>
      </Box>
    </Dialog>
  );
}

// ============================================================
// Delete Confirmation Dialog
// ============================================================
export function DeleteConfirmDialog({
  open, item, onClose, onConfirm,
}: {
  open: boolean;
  item: DataSourceItem | null;
  onClose: () => void;
  onConfirm: () => void;
}) {
  const theme = useTheme();
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <Box sx={{ p: 3 }}>
        <Stack spacing={2}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box sx={{ p: 1, borderRadius: 2, bgcolor: alpha(theme.palette.error.main, 0.1), display: 'flex' }}>
              <Trash2 size={24} color={theme.palette.error.main} />
            </Box>
            <Typography variant="h6" fontWeight={600}>Remove Data Source?</Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            Are you sure you want to remove this data source?
          </Typography>
          {item && (
            <Box sx={{ p: 2, borderRadius: 1, bgcolor: alpha(theme.palette.error.main, 0.05), border: '1px solid', borderColor: alpha(theme.palette.error.main, 0.2) }}>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                {item.url}
              </Typography>
            </Box>
          )}
          <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 2 }}>
            <Button variant="outlined" onClick={onClose} startIcon={<X size={18} />}>Cancel</Button>
            <Button variant="contained" color="error" onClick={onConfirm} startIcon={<Trash2 size={18} />}>Remove</Button>
          </Stack>
        </Stack>
      </Box>
    </Dialog>
  );
}

// ============================================================
// Delete Integration Confirm Dialog (SonarCloud / BlackDuck)
// ============================================================
export function DeleteIntegrationConfirmDialog({
  open, platform, url, onClose, onConfirm,
}: {
  open: boolean;
  platform: string;
  url: string;
  onClose: () => void;
  onConfirm: () => void;
}) {
  const theme = useTheme();
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <Box sx={{ p: 3 }}>
        <Stack spacing={2}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box sx={{ p: 1, borderRadius: 2, bgcolor: alpha(theme.palette.error.main, 0.1), display: 'flex' }}>
              <Trash2 size={24} color={theme.palette.error.main} />
            </Box>
            <Typography variant="h6" fontWeight={600}>Remove {platform} Integration?</Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            This will permanently remove the {platform} integration, including the saved token and connection configuration. This action cannot be undone.
          </Typography>
          {url && (
            <Box sx={{ p: 2, borderRadius: 1, bgcolor: alpha(theme.palette.error.main, 0.05), border: '1px solid', borderColor: alpha(theme.palette.error.main, 0.2) }}>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                {url}
              </Typography>
            </Box>
          )}
          <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 2 }}>
            <Button variant="outlined" onClick={onClose} startIcon={<X size={18} />}>Cancel</Button>
            <Button variant="contained" color="error" onClick={onConfirm} startIcon={<Trash2 size={18} />}>Remove</Button>
          </Stack>
        </Stack>
      </Box>
    </Dialog>
  );
}

// ============================================================
// Git Access Confirmation Dialog
// ============================================================
export function GitAccessDialog({
  open, validating, error, url, authMode = 'pid', onClose, onConfirm,
}: {
  open: boolean;
  validating: boolean;
  error: string;
  url: string;
  authMode?: 'pid' | 'pat';
  onClose: () => void;
  onConfirm: () => void;
}) {
  const theme = useTheme();
  const isPat = authMode === 'pat';
  return (
    <Dialog open={open} onClose={() => !validating && onClose()} maxWidth="sm" fullWidth>
      <Box sx={{ p: 3 }}>
        <Stack spacing={2.5}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box sx={{ p: 1, borderRadius: 2, bgcolor: alpha(theme.palette.warning.main, 0.1), display: 'flex' }}>
              <Shield size={24} color={theme.palette.warning.main} />
            </Box>
            <Typography variant="h6" fontWeight={600}>
              {isPat ? 'Validate Repository Access' : 'Repository Access Required'}
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            {isPat ? (
              <>Validating access to this repository using your <strong>Personal Access Token</strong>. Your PAT must have a minimum of <strong>Read access</strong>.</>
            ) : (
              <>Please ensure PID user <strong>PIDF6B7</strong> of <strong>Sentinel</strong> has{' '}a minimum of <strong>Read access</strong> to your repository.</>
            )}
          </Typography>
          <Box sx={{ p: 2, borderRadius: 1, bgcolor: alpha(theme.palette.info.main, 0.05), border: '1px solid', borderColor: alpha(theme.palette.info.main, 0.2) }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              A minimum of Read access is required for code analysis.
              Write access is required <strong>only</strong> to create branches and raise Pull Requests.
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all', mt: 1 }}>
              {url}
            </Typography>
          </Box>
          {error && (
            <Alert severity="error" icon={<AlertCircle size={20} />}>
              {error}
            </Alert>
          )}
          <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 1 }}>
            <Button variant="outlined" onClick={onClose} disabled={validating} startIcon={<X size={18} />}>Cancel</Button>
            <Button
              variant="contained"
              onClick={onConfirm}
              disabled={validating}
              startIcon={validating ? <CircularProgress size={18} color="inherit" /> : <Shield size={18} />}
            >
              {validating ? 'Validating...' : isPat ? 'Validate with PAT' : 'Confirm & Validate'}
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Dialog>
  );
}
